import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { Badge, Button, Card, Modal } from 'react-bootstrap';
import toastr from '../../../toastr';
import Model from '../../model/ShopKeeper.model';

interface IProps {
  container: Object;
  hide: Function;
  model: Model;
  show: boolean;
  moveToOperationMode?: boolean;
}

const operationSelectorModal: FunctionComponent<IProps> = ({ container, hide, model, show, moveToOperationMode }) => {
  const operationDetails = model.activeRepairOrderDetails.operationDetails;

  const selectOption = (operationNumber: number) => {
    if (moveToOperationMode) {
      if (operationDetails[operationNumber].locked) {
        toastr.error('Error', 'Unable to move to locked operation.');
      } else if (operationDetails[operationNumber].voided) {
        toastr.error('Error', 'Unable to move to voided operation.');
      } else {
        hide();
        model.moveOperationsSetDestinationOperationNumber(operationNumber);
      }

      // Set Active Operation Logic
    } else {
      hide();
      model.setActiveOperationNumber(operationNumber);
    }
  };

  return (
    <Modal
      backdrop="static"
      container={container as any}
      dialogClassName="sk-operationSelectorModal"
      onHide={hide as any}
      size="lg"
      show={show}
    >
      <Modal.Header closeButton={true}>
        <Modal.Title>{moveToOperationMode ? 'Select Destination Operation' : 'Set Active Operation'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <table className="sk-operationSelectionContainer" onClick={() => selectOption(0)}>
          <tbody>
            <tr className="sk-operationSelection">
              <td className="sk-operationSelectionNumber">S</td>
              <td className="sk-operationSelectionDetails">
                <Card className="success">
                  <Card.Header>
                    <Card.Title>Summary View</Card.Title>
                  </Card.Header>
                  <Card.Body>Show All Operations</Card.Body>
                </Card>
              </td>
            </tr>
          </tbody>
        </table>

        {operationDetails.map((operationDetail, operationNumber) => {
          // Does this operation have any pre-issued parts in the queue?
          const queuedPartsForIssue = model.lineItems.some(lineItem => {
            return !lineItem.issuedToRO && lineItem.roDetail && lineItem.roDetail.operationNumber === operationNumber;
          });

          return (
            <table
              key={`operation${operationNumber}`}
              className="sk-operationSelectionContainer"
              onClick={() => selectOption(operationNumber)}
            >
              <tbody>
                <tr className="sk-operationSelection">
                  <td
                    className={classNames({
                      'sk-operationSelectionNumber': true,
                      'sk-operationSelectionNumberLocked': operationDetail.locked || operationDetail.voided,
                    })}
                  >
                    {operationNumber}
                    {operationDetail.locked && (
                      <div>
                        <i className="zmdi zmdi-lock" />
                      </div>
                    )}
                    {operationDetail.voided && <div className="sk-voidedOperation">VOIDED</div>}
                  </td>
                  <td className="sk-operationSelectionDetails">
                    <Card
                      className={operationDetail.laborType && operationDetail.secondaryLaborType ? 'info' : 'success'}
                    >
                      <Card.Header>
                        <Card.Title>
                          <div>
                            {operationDetail.laborType}
                            {operationDetail.laborTypeDescription ? ` - ${operationDetail.laborTypeDescription}` : ''}
                          </div>

                          {operationDetail.secondaryLaborType ? (
                            <div>
                              {operationDetail.secondaryLaborType}
                              {operationDetail.secondaryLaborTypeDescription
                                ? ` - ${operationDetail.secondaryLaborTypeDescription}`
                                : ''}
                            </div>
                          ) : null}

                          {operationDetail.warranty && <Badge variant="primary">Warranty</Badge>}
                        </Card.Title>
                      </Card.Header>
                      <Card.Body>
                        {queuedPartsForIssue && (
                          <div className="sk-operationHasQueuedParts">
                            <i className="zmdi zmdi-alert-polygon" /> This operation has items queued for issue!
                          </div>
                        )}
                        {operationDetail.conditionDescLines.map((line, idx) => (
                          <span key={`conditionLine${idx + 1}`}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </Card.Body>
                    </Card>
                  </td>
                </tr>
              </tbody>
            </table>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => hide()}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default operationSelectorModal;
